import { z } from "zod";
import { FORM_FIELD_CADENCES, FORM_FIELD_TYPES, INSPECTION_REVIEW_STATES, INSPECTION_TYPES, LOCATION_TYPES } from "../enums.ts";
import { DateOnly, TimeOnly, Money } from "./common.ts";
import { RangeInput } from "./seals.ts";

export const FormFieldInput = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{1,60}$/),
  label: z.string().min(1).max(300),
  fieldType: z.enum(FORM_FIELD_TYPES),
  required: z.boolean().default(false),
  dualEntry: z.boolean().default(false),
  order: z.number().int().min(0).default(0),
  options: z.array(z.string()).nullable().optional(),
  columns: z.array(z.object({ key: z.string(), label: z.string(), mono: z.boolean().optional() })).nullable().optional(),
  helpText: z.string().max(400).nullable().optional(),
  section: z.string().max(120).nullable().optional(),
  cadence: z.enum(FORM_FIELD_CADENCES).default("every_visit"),
  cadenceMonths: z.number().int().min(1).max(60).nullable().optional(),
  /** MM-DD: an annual question anchored to a fixed date is asked again once that date passes */
  anchorDate: z.string().regex(/^(0[1-9]|1[0-2])-(0[1-9]|[12]d|3[01])$/).nullable().optional(),
  carryForward: z.boolean().default(false),
  /** When answered with this value the report raises an issue automatically. */
  concernWhen: z.string().max(60).nullable().optional(),
  showWhen: z.object({ field: z.string(), equals: z.string() }).nullable().optional(),
});
export type FormFieldInput = z.infer<typeof FormFieldInput>;

export const FormTemplateInput = z.object({
  productionTypeId: z.string().uuid().nullable().optional(),
  recordKind: z.enum(["inspection", "certification_report", "packaging_record"]).default("inspection"),
  inspectionType: z.enum(INSPECTION_TYPES).nullable().optional(),
  name: z.string().min(1).max(200),
  fields: z.array(FormFieldInput),
});
export type FormTemplateInput = z.infer<typeof FormTemplateInput>;

export const ProductionInput = z.object({
  locationType: z.enum(LOCATION_TYPES),
  locationId: z.string().uuid(),
  startsOn: DateOnly,
  endsOn: DateOnly,
  productionTypeId: z.string().uuid().nullable().optional(),
  companyIds: z.array(z.string().uuid()).min(1),
  mashgiachPersonIds: z.array(z.string().uuid()).default([]),
  products: z.array(z.object({ productId: z.string().uuid(), quantity: z.string().max(60).nullable().optional(), lotCode: z.string().max(80).nullable().optional() })).default([]),
  notes: z.string().max(4000).nullable().optional(),
  /** the wine project this supervised day belongs to */
  wineProjectId: z.string().uuid().nullable().optional(),
  status: z.enum(["scheduled", "in_progress", "completed", "cancelled"]).default("scheduled"),
  /** repeat this run on a cadence (a frequency key) until a date — the weekly job creates the occurrences ahead */
  recurrenceRule: z.string().max(40).nullable().optional(),
  recurrenceUntil: DateOnly.nullable().optional(),
  /** open POs for the location, or new ones entered inline */
  purchaseOrderIds: z.array(z.string().uuid()).default([]),
  newPurchaseOrders: z.array(z.object({ companyId: z.string().uuid(), poNumber: z.string().min(1).max(80), amount: Money.nullable().optional() })).default([]),
});
export type ProductionInput = z.infer<typeof ProductionInput>;
export const ProductionUpdateInput = ProductionInput.partial();

export const ProductionQuery = z.object({
  companyId: z.string().uuid().optional(),
  locationType: z.enum(LOCATION_TYPES).optional(),
  locationId: z.string().uuid().optional(),
  from: DateOnly.optional(),
  to: DateOnly.optional(),
  status: z.string().max(40).optional(),
  mine: z.string().optional().transform((v) => v === "1" || v === "true"),
  wineProjectId: z.string().uuid().optional(),
});

export const InspectionProductRow = z.object({
  product: z.string().max(300),
  quantity: z.string().max(80).nullable().optional(),
  lot: z.string().max(80).nullable().optional(),
  enjoyBy: z.string().max(40).nullable().optional(),
});

export const InspectionInput = z.object({
  type: z.enum(INSPECTION_TYPES),
  isSpecial: z.boolean().default(false),
  companyId: z.string().uuid(),
  facilityId: z.string().uuid().nullable().optional(),
  plantId: z.string().uuid().nullable().optional(),
  productionId: z.string().uuid().nullable().optional(),
  eventId: z.string().uuid().nullable().optional(),
  mashgiachPersonId: z.string().uuid().nullable().optional(),
  date: DateOnly,
  startTime: TimeOnly.nullable().optional(),
  endTime: TimeOnly.nullable().optional(),
  invoiceRequired: z.boolean().default(true),
  hideFromOthers: z.boolean().default(false),
  initialInspection: z.boolean().default(false),
  templateId: z.string().uuid().nullable().optional(),
  /** Answers keyed by form field key. Tables are arrays of row objects. */
  answers: z.record(z.string(), z.unknown()).default({}),
  products: z.array(InspectionProductRow).default([]),
  issue: z.boolean().default(false),
  issueDescription: z.string().max(4000).nullable().optional(),
  issueSeverity: z.string().max(40).nullable().optional(),
  resolvedStatus: z.string().max(60).nullable().optional(),
  /** A declaration the filer is making — recorded as an attestation on the response, never typed twice. */
  attestation: z.object({ title: z.string().max(120).nullable().optional() }).nullable().optional(),
  notes: z.string().max(8000).nullable().optional(),
  reviewState: z.enum(INSPECTION_REVIEW_STATES).default("filed"),
  /** client-generated id for offline idempotency */
  clientRef: z.string().max(80).nullable().optional(),
  /** seals applied on this production, from the mashgiach's own roll; validated against what he holds */
  seals: z.array(z.object({ rollId: z.string().uuid().nullable().optional(), range: RangeInput })).default([]),
});
export type InspectionInput = z.infer<typeof InspectionInput>;
export const InspectionUpdateInput = InspectionInput.partial();

export const InspectionQuery = z.object({
  q: z.string().max(200).optional(),
  companyId: z.string().uuid().optional(),
  type: z.enum(INSPECTION_TYPES).optional(),
  reviewState: z.enum(INSPECTION_REVIEW_STATES).optional(),
  mashgiachPersonId: z.string().uuid().optional(),
  from: DateOnly.optional(),
  to: DateOnly.optional(),
  issuesOnly: z.string().optional().transform((v) => v === "1" || v === "true"),
});
